// Selector de posiciones dentro de cada grupo del Mundial
// Permite al usuario arrastrar/seleccionar el orden final de cada grupo
import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronUp, ChevronDown, Check, GripVertical } from 'lucide-react'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Flag from '../ui/Flag'

// Componente para un grupo individual
function GroupCard({ group, predictions, onUpdatePrediction, isComplete }) {
  // Mover equipo hacia arriba en la clasificación
  const moveUp = (index) => {
    if (index === 0) return
    const newOrder = [...predictions]
    const temp = newOrder[index]
    newOrder[index] = newOrder[index - 1]
    newOrder[index - 1] = temp
    onUpdatePrediction(group.nombre, newOrder)
  }

  // Mover equipo hacia abajo
  const moveDown = (index) => {
    if (index === predictions.length - 1) return
    const newOrder = [...predictions]
    const temp = newOrder[index]
    newOrder[index] = newOrder[index + 1]
    newOrder[index + 1] = temp
    onUpdatePrediction(group.nombre, newOrder)
  }

  return (
    <Card variant={isComplete ? 'cyan' : 'default'} hover={false} padding="none">
      <div className="p-4 border-b border-border-subtle flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-lg text-cyan">Grupo {group.nombre}</span>
          {isComplete && <Check className="w-4 h-4 text-success" />}
        </div>
        <Badge variant={isComplete ? 'success' : 'default'} size="sm">
          {isComplete ? 'Listo' : 'Pendiente'}
        </Badge>
      </div>
      <div className="p-2">
        {predictions.map((team, index) => (
          <motion.div
            key={team.id || index}
            layout
            className={`
              flex items-center gap-2 px-3 py-2.5 rounded-lg mb-1
              ${index < 2 ? 'bg-success/5 border border-success/10' : index === 2 ? 'bg-warning/5 border border-warning/10' : 'bg-surface-card'}
            `}
          >
            {/* Posición */}
            <span className={`
              font-mono text-xs font-bold w-5 text-center
              ${index < 2 ? 'text-success' : index === 2 ? 'text-warning' : 'text-text-muted'}
            `}>
              {index + 1}
            </span>

            {/* Ícono de arrastre */}
            <GripVertical className="w-3 h-3 text-text-muted shrink-0" />

            {/* Nombre del equipo */}
            <span className="flex-1 text-sm font-medium text-text-primary truncate flex items-center">
              <Flag code={team.bandera} className="w-4 h-3 mr-1.5 object-cover rounded shadow-sm shrink-0" />
              <span>{team.nombre}</span>
            </span>

            {/* Etiqueta de clasificación */}
            {index < 2 && (
              <span className="text-[0.6rem] font-bold uppercase tracking-wider text-success">
                Clasifica
              </span>
            )}
            {index === 2 && (
              <span className="text-[0.6rem] font-bold uppercase tracking-wider text-warning">
                3°
              </span>
            )}

            {/* Controles de movimiento */}
            <div className="flex flex-col">
              <button
                onClick={() => moveUp(index)}
                disabled={index === 0}
                className="p-0.5 text-text-muted hover:text-cyan disabled:opacity-20 transition-colors"
                aria-label={`Subir ${team.nombre}`}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => moveDown(index)}
                disabled={index === predictions.length - 1}
                className="p-0.5 text-text-muted hover:text-cyan disabled:opacity-20 transition-colors"
                aria-label={`Bajar ${team.nombre}`}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Leyenda */}
      <div className="px-4 pb-3 flex gap-3 text-[0.6rem] text-text-muted">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-success" /> Pasa a octavos
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-warning" /> Mejor tercero
        </span>
      </div>
    </Card>
  )
}

export default function GroupPicker({ predictions, onUpdate, groups = [] }) {
  // Inicializar predicciones con el orden original si no existen
  const getGroupPredictions = useCallback((groupName) => {
    const group = groups.find(g => g.nombre === groupName)
    return predictions?.[groupName] || group?.equipos || []
  }, [predictions, groups])

  // Verificar si un grupo está completo
  const isGroupComplete = (groupName) => {
    return !!predictions?.[groupName]
  }

  // Contar grupos completados
  const completedCount = groups.filter(g => isGroupComplete(g.nombre)).length

  return (
    <div>
      {/* Encabezado con progreso */}
      <div className="mb-6">
        <h3 className="text-xl font-display font-bold text-text-primary mb-2">
          Fase de Grupos
        </h3>
        <p className="text-sm text-text-secondary mb-3">
          Ordena los equipos de cada grupo según tu predicción. Los primeros 2 clasifican directo a octavos.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-surface-card rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-cyan rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / (groups.length || 12)) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <span className="text-sm font-mono text-cyan font-semibold">
            {completedCount}/{groups.length || 12}
          </span>
        </div>
      </div>

      {/* Cuadrícula de grupos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {groups.map((group, i) => (
            <motion.div
              key={group.nombre}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <GroupCard
                group={group}
                predictions={getGroupPredictions(group.nombre)}
                onUpdatePrediction={(name, order) => {
                  onUpdate({ ...predictions, [name]: order })
                }}
                isComplete={isGroupComplete(group.nombre)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}
